import type { PaginatedResponse } from '@/types/Api'
import type { UUID } from '@/types/Id'
import type { EntityType } from '@/types/library/Entity'

interface LibrarySyncStatusQueryKeyArgs {
  orgId: UUID | ''
}

export const librarySyncQueryKeys = {
  all: () => ['library_sync'] as const,
  orgSyncStatus: (keyArgs: LibrarySyncStatusQueryKeyArgs) =>
    ['library_sync_status', keyArgs.orgId] as const,
}

// region: library v2

export interface MutationContext<T> {
  list: PaginatedResponse<T> | undefined
  entity: T | undefined
}

export interface LibraryEntityCount {
  count: number
  type: EntityType
}

interface LibraryPeopleQueryKeyArgs {
  orgId: UUID | ''
  withConfirmed?: boolean
  withSuggested?: boolean
}

interface LibraryPersonQueryKeyArgs {
  orgId: UUID | ''
  personId: UUID | ''
}

export const libraryPeopleQueryKeys = {
  all: () => ['library_people'] as const,
  organization: (keyArgs: LibraryPeopleQueryKeyArgs) =>
    [
      'library_people',
      keyArgs.orgId,
      ...(keyArgs.withConfirmed !== undefined
        ? (['with_confirmed', keyArgs.withConfirmed] as const)
        : ([] as const)),
      ...(keyArgs.withSuggested !== undefined
        ? (['with_suggested', keyArgs.withSuggested] as const)
        : ([] as const)),
    ] as const,
  count: (keyArgs: LibraryPeopleQueryKeyArgs) =>
    [
      'library_people',
      keyArgs.orgId,
      'count',
      ...(keyArgs.withConfirmed !== undefined
        ? (['with_confirmed', keyArgs.withConfirmed] as const)
        : ([] as const)),
      ...(keyArgs.withSuggested !== undefined
        ? (['with_suggested', keyArgs.withSuggested] as const)
        : ([] as const)),
    ] as const,
  person: (keyArgs: LibraryPersonQueryKeyArgs) =>
    ['library_people', keyArgs.orgId, keyArgs.personId] as const,
}

interface LibraryCompanyPeopleQueryKeyArgs {
  orgId: UUID | ''
  companyId: UUID | ''
}

export const libraryCompanyPeopleQueryKeys = {
  company: (keyArgs: LibraryCompanyPeopleQueryKeyArgs) =>
    ['library_people', keyArgs.orgId, keyArgs.companyId] as const,
  count: (keyArgs: LibraryCompanyPeopleQueryKeyArgs) =>
    ['library_people', keyArgs.orgId, keyArgs.companyId, 'count'] as const,
}

interface LibraryDepartmentsQueryKeyArgs {
  orgId: UUID | ''
  companyId: UUID | ''
}

interface LibraryAllDepartmentsQueryKeyArgs {
  orgId: UUID | ''
}

export const libraryDepartmentsQueryKeys = {
  all: () => ['library_departments'] as const,
  organization: (keyArgs: LibraryAllDepartmentsQueryKeyArgs) =>
    ['library_departments', keyArgs.orgId] as const,
  company: (keyArgs: LibraryDepartmentsQueryKeyArgs) =>
    ['library_departments', keyArgs.orgId, keyArgs.companyId] as const,
  count: (keyArgs: LibraryDepartmentsQueryKeyArgs) =>
    ['library_departments', keyArgs.orgId, keyArgs.companyId, 'count'] as const,
}

export const libraryAllDepartmentsQueryKeys = {
  count: (keyArgs: LibraryAllDepartmentsQueryKeyArgs) =>
    ['library_departments', keyArgs.orgId, 'count'] as const,
}

interface LibraryDepartmentQueryKeyArgs {
  orgId: UUID | ''
  companyId: UUID | ''
  departmentId: UUID | ''
}

interface LibraryDepartmentAnyCompanyQueryKeyArgs {
  orgId: UUID | ''
  departmentId: UUID | ''
}

export const libraryDepartmentQueryKeys = {
  department: (keyArgs: LibraryDepartmentQueryKeyArgs) =>
    [
      'library_department',
      keyArgs.orgId,
      keyArgs.companyId,
      keyArgs.departmentId,
    ] as const,
  // Single-department keys are scoped by company at index 2, so a prefix match
  // can't isolate a department across companies. Use this predicate to match
  // any cached `department` key for the given org + department, regardless of
  // which company id sits in between.
  matchesAnyCompany:
    (keyArgs: LibraryDepartmentAnyCompanyQueryKeyArgs) =>
    (queryKey: readonly unknown[]): boolean =>
      queryKey[0] === 'library_department' &&
      queryKey[1] === keyArgs.orgId &&
      queryKey[3] === keyArgs.departmentId,
}

interface LibraryCompaniesQueryKeyArgs {
  orgId: UUID | ''
  withConfirmed?: boolean
  withSuggested?: boolean
}

interface LibraryCompanyRelationshipsQueryKeyArgs {
  orgId: UUID | ''
}

export const libraryCompaniesQueryKeys = {
  all: () => ['library_company'] as const,
  organization: (keyArgs: LibraryCompaniesQueryKeyArgs) =>
    [
      'library_company',
      keyArgs.orgId,
      ...(keyArgs.withConfirmed !== undefined
        ? (['with_confirmed', keyArgs.withConfirmed] as const)
        : ([] as const)),
      ...(keyArgs.withSuggested !== undefined
        ? (['with_suggested', keyArgs.withSuggested] as const)
        : ([] as const)),
    ] as const,
  count: (keyArgs: LibraryCompaniesQueryKeyArgs) =>
    [
      'library_company',
      keyArgs.orgId,
      'count',
      ...(keyArgs.withConfirmed !== undefined
        ? (['with_confirmed', keyArgs.withConfirmed] as const)
        : ([] as const)),
      ...(keyArgs.withSuggested !== undefined
        ? (['with_suggested', keyArgs.withSuggested] as const)
        : ([] as const)),
    ] as const,
  relationships: (keyArgs: LibraryCompanyRelationshipsQueryKeyArgs) =>
    ['library_company', keyArgs.orgId, 'relationships'] as const,
}

// region: entity relationships

interface LibraryEntityRelationshipsQueryKeyArgs {
  orgId: UUID | ''
  entityId: UUID | ''
  loadAll?: boolean
}

export const libraryEntityRelationshipsQueryKeys = {
  all: () => ['library_entity_relationships'] as const,
  entity: (keyArgs: LibraryEntityRelationshipsQueryKeyArgs) =>
    [
      'library_entity_relationships',
      keyArgs.orgId,
      keyArgs.entityId,
      keyArgs.loadAll ? 'load_all' : 'ids_only',
    ] as const,
}

interface LibraryEntityFileRelationshipsQueryKeyArgs {
  orgId: UUID | ''
  entityId: UUID | ''
}

export const libraryEntityFileRelationshipsQueryKeys = {
  all: () => ['library_entity_file_relationships'] as const,
  entity: (keyArgs: LibraryEntityFileRelationshipsQueryKeyArgs) =>
    [
      'library_entity_file_relationships',
      keyArgs.orgId,
      keyArgs.entityId,
    ] as const,
}

// endregion

interface LibraryCompanyQueryKeyArgs {
  orgId: UUID | ''
  companyId: UUID | ''
}

export const libraryCompanyQueryKeys = {
  company: (keyArgs: LibraryCompanyQueryKeyArgs) =>
    ['library_company', keyArgs.orgId, keyArgs.companyId] as const,
}

interface LibraryEntitySuggestedQueryKeyArgs {
  orgId: UUID | ''
  entityTypes?: EntityType[]
}

export const libraryEntitySuggestedQueryKeys = {
  all: () => ['library_entity_suggested'] as const,
  organization: (keyArgs: LibraryEntitySuggestedQueryKeyArgs) =>
    [
      'library_entity_suggested',
      keyArgs.orgId,
      ...(keyArgs.entityTypes ?? []),
    ] as const,
}

interface LibraryAgencyQueryKeyArgs {
  orgId: UUID | ''
  withConfirmed?: boolean
  withSuggested?: boolean
}

interface LibraryAgencyEntityQueryKeyArgs {
  orgId: UUID | ''
  agencyId: UUID | ''
}

export const libraryAgencyQueryKeys = {
  all: () => ['library_agency'] as const,
  organization: (keyArgs: LibraryAgencyQueryKeyArgs) =>
    [
      'library_agency',
      keyArgs.orgId,
      keyArgs.withConfirmed,
      keyArgs.withSuggested,
    ] as const,
  agency: (keyArgs: LibraryAgencyEntityQueryKeyArgs) =>
    ['library_agency', keyArgs.orgId, keyArgs.agencyId] as const,
  count: (keyArgs: LibraryAgencyQueryKeyArgs) =>
    ['library_agency', keyArgs.orgId, 'count'] as const,
}

interface LibraryEntitySchemasQueryKeyArgs {
  orgId: UUID | ''
}

export const libraryEntitySchemasQueryKeys = {
  all: () => ['library_entity_schemas'] as const,
  organization: (keyArgs: LibraryEntitySchemasQueryKeyArgs) =>
    ['library_entity_schemas', keyArgs.orgId] as const,
}

export type LibrarySortOption = 'name' | 'last_modified' | 'created_at'
export type LibrarySortDirection = 'asc' | 'desc'

interface LibraryEntitiesQueryKeyArgs {
  orgId: UUID | ''
  entityTypes?: EntityType[]
  entityCompanyId?: UUID | ''
  limit?: number
  offset?: number
  sort?: LibrarySortOption
  order?: LibrarySortDirection
  q?: string
  withConfirmed?: boolean
  withSuggested?: boolean
}

export const libraryEntitiesQueryKeys = {
  all: () => ['library_entities'] as const,
  organization: (keyArgs: LibraryEntitiesQueryKeyArgs) =>
    [
      'library_entities',
      keyArgs.orgId,
      ...(keyArgs.entityTypes && keyArgs.entityTypes.length
        ? (['types', ...keyArgs.entityTypes] as const)
        : ([] as const)),
      ...(keyArgs.limit ? (['limit', keyArgs.limit] as const) : ([] as const)),
      ...(keyArgs.offset
        ? (['offset', keyArgs.offset] as const)
        : ([] as const)),
      ...(keyArgs.sort ? (['sort', keyArgs.sort] as const) : ([] as const)),
      ...(keyArgs.order ? (['order', keyArgs.order] as const) : ([] as const)),
      ...(keyArgs.q ? (['q', keyArgs.q] as const) : ([] as const)),
      ...(keyArgs.withConfirmed !== undefined
        ? (['with_confirmed', keyArgs.withConfirmed] as const)
        : ([] as const)),
      ...(keyArgs.withSuggested !== undefined
        ? (['with_suggested', keyArgs.withSuggested] as const)
        : ([] as const)),
    ] as const,
}

interface LibraryCatalogEntriesQueryKeyArgs {
  orgId: UUID | ''
  limit?: number
  offset?: number
  sort?: LibrarySortOption
  order?: LibrarySortDirection
  q?: string
  withConfirmed?: boolean
  withSuggested?: boolean
}

interface LibrarySchemaEntriesQueryKeyArgs {
  orgId: UUID | ''
  schemaId: UUID | ''
  limit?: number
  offset?: number
  sort?: LibrarySortOption
  order?: LibrarySortDirection
  q?: string
  withConfirmed?: boolean
  withSuggested?: boolean
}

interface LibraryCatalogEntryQueryKeyArgs {
  orgId: UUID | ''
  catalogEntryId: UUID | ''
}

export const libraryCatalogEntriesQueryKeys = {
  all: () => ['library_catalog_entries'] as const,
  organization: (keyArgs: LibraryCatalogEntriesQueryKeyArgs) =>
    [
      'library_catalog_entries',
      keyArgs.orgId,
      ...(keyArgs.limit ? ['limit', keyArgs.limit] : []),
      ...(keyArgs.offset ? ['offset', keyArgs.offset] : []),
      ...(keyArgs.sort ? ['sort', keyArgs.sort] : []),
      ...(keyArgs.order ? ['order', keyArgs.order] : []),
      ...(keyArgs.q ? ['q', keyArgs.q] : []),
      ...(keyArgs.withConfirmed !== undefined
        ? ['with_confirmed', keyArgs.withConfirmed]
        : []),
      ...(keyArgs.withSuggested !== undefined
        ? ['with_suggested', keyArgs.withSuggested]
        : []),
    ] as const,
  count: (keyArgs: LibraryCatalogEntriesQueryKeyArgs) =>
    [
      'library_catalog_entries',
      keyArgs.orgId,
      'count',
      ...(keyArgs.withConfirmed !== undefined
        ? (['with_confirmed', keyArgs.withConfirmed] as const)
        : ([] as const)),
      ...(keyArgs.withSuggested !== undefined
        ? (['with_suggested', keyArgs.withSuggested] as const)
        : ([] as const)),
    ] as const,
  suggestedCount: (keyArgs: LibraryCatalogEntriesQueryKeyArgs) =>
    [
      'library_catalog_entries',
      keyArgs.orgId,
      'count',
      'suggested',
      ...(keyArgs.withConfirmed !== undefined
        ? (['with_confirmed', keyArgs.withConfirmed] as const)
        : ([] as const)),
      ...(keyArgs.withSuggested !== undefined
        ? (['with_suggested', keyArgs.withSuggested] as const)
        : ([] as const)),
    ] as const,
  schema: (keyArgs: LibrarySchemaEntriesQueryKeyArgs) =>
    [
      'library_catalog_entries',
      keyArgs.orgId,
      'schema',
      keyArgs.schemaId,
      ...(keyArgs.limit ? ['limit', keyArgs.limit] : []),
      ...(keyArgs.offset ? ['offset', keyArgs.offset] : []),
      ...(keyArgs.sort ? ['sort', keyArgs.sort] : []),
      ...(keyArgs.order ? ['order', keyArgs.order] : []),
      ...(keyArgs.q ? ['q', keyArgs.q] : []),
      ...(keyArgs.withConfirmed !== undefined
        ? ['with_confirmed', keyArgs.withConfirmed]
        : []),
      ...(keyArgs.withSuggested !== undefined
        ? ['with_suggested', keyArgs.withSuggested]
        : []),
    ] as const,
  schemaPrefix: (keyArgs: LibrarySchemaEntriesQueryKeyArgs) =>
    [
      'library_catalog_entries',
      keyArgs.orgId,
      'schema',
      keyArgs.schemaId,
    ] as const,
  schemaCount: (keyArgs: LibrarySchemaEntriesQueryKeyArgs) =>
    [
      'library_catalog_entries',
      keyArgs.orgId,
      'schema',
      keyArgs.schemaId,
      'count',
      ...(keyArgs.withConfirmed !== undefined
        ? (['with_confirmed', keyArgs.withConfirmed] as const)
        : ([] as const)),
      ...(keyArgs.withSuggested !== undefined
        ? (['with_suggested', keyArgs.withSuggested] as const)
        : ([] as const)),
    ] as const,
  entry: (keyArgs: LibraryCatalogEntryQueryKeyArgs) =>
    [
      'library_catalog_entries',
      keyArgs.orgId,
      'entry',
      keyArgs.catalogEntryId,
    ] as const,
}

// region: org company profile

interface OrgCompanyProfileQueryKeyArgs {
  orgId: UUID | ''
}

export const orgCompanyProfileQueryKeys = {
  all: () => ['org_company_profile'] as const,
  organization: (keyArgs: OrgCompanyProfileQueryKeyArgs) =>
    ['org_company_profile', keyArgs.orgId] as const,
}

// endregion: org company profile

// region: entity file citations

interface EntityFileCitationsQueryKeyArgs {
  orgId: UUID | ''
  fileId: UUID | ''
}

export const entityFileCitationsQueryKeys = {
  all: () => ['entity_file_citations'] as const,
  file: (keyArgs: EntityFileCitationsQueryKeyArgs) =>
    ['entity_file_citations', keyArgs.orgId, keyArgs.fileId] as const,
}

// endregion: entity file citations

// region: entity activities

interface EntityActivitiesQueryKeyArgs {
  orgId: UUID | ''
  entityId: UUID | ''
  activityType?: string
  limit?: number
  offset?: number
}

export const entityActivitiesQueryKeys = {
  all: () => ['entity_activities'] as const,
  entity: (keyArgs: EntityActivitiesQueryKeyArgs) =>
    [
      'entity_activities',
      keyArgs.orgId,
      keyArgs.entityId,
      ...(keyArgs.activityType ? ['type', keyArgs.activityType] : []),
      ...(keyArgs.limit !== undefined ? ['limit', keyArgs.limit] : []),
      ...(keyArgs.offset !== undefined ? ['offset', keyArgs.offset] : []),
    ] as const,
}

// endregion: entity activities

// endregion: library v2
